import getActiveComp from 'common/src/modules/creativeTemplates/copilot/agents/adBuilderAgent/utils/getActiveComp';
import {NumberArr} from 'common/src/modules/movie/moviePrimitives.types';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {AdBuilderAgentToolName} from '../adBuilderAgent.enums';

import {buildKeyframedValue} from '../../../utils/buildKeyframedValue';
import {AnimatedKeyframeInput} from '../adBuilderAgent.types';

import updateLayer from 'common/src/modules/creativeTemplates/store/creativeTemplatePage/formatMovies/actions/updateLayer';
import {MovieLayer} from 'common/src/modules/movie/movieLayers.types';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {parseTransformFromNaturalLanguage} from '../../utils/parseTransformFromNaturalLanguage';

// Simple input schema for natural language
const inputSchema = z.object({
  layerId: z.string().describe('ID of the layer to update'),
  transformExplained: z.string().describe(
    `Natural language description of the transform changes needed 
      (e.g., "move to top right corner and fade out over 2 seconds", "rotate 90 degrees clockwise", "scale to 150% and move down").
      You can also provide with reference to composition size (e.g., "move to 80,70% of composition", "move to center of the composition")`,
  ),
});

function editLayerTransform2Tool(store: CLStore) {
  return tool({
    name: AdBuilderAgentToolName.EditLayerTransform2,
    displayName: 'Editing layer transform',
    description: `Updates layer transform properties using natural language descriptions.
                  Converts requests like "position at 30,50" or 'scale 70,70% at time 1 sec to 100%,100% at time 3.3 sec with easing on start and end' into structured transform parameters.
                  Supports position, anchor, scale, rotation (all in X,Y,Z), and opacity changes.
                  Can handle both static changes and keyframe animations. send 2D values unless 3D needed`,
    inputSchema,
    execute: async ({
      input: params,
      abortSignal,
    }: ToolExecuteParams<typeof inputSchema>): Promise<{
      content: string;
      error?: string;
    }> => {
      const activeComp = getActiveComp(store);

      if (!activeComp) {
        return {
          content: 'ERROR: No active composition found',
          error: 'No active composition found',
        };
      }

      try {
        const layer = activeComp.l.find((l) => l.id === params.layerId);

        if (!layer) {
          return {
            content: JSON.stringify(
              {error: `Layer with ID "${params.layerId}" not found`},
              null,
              2,
            ),
            error: `Layer with ID "${params.layerId}" not found`,
          };
        }

        if (layer.isLocked === true) {
          return {
            content: JSON.stringify(
              {error: `Layer "${layer.name}" is locked and cannot be modified`},
              null,
              2,
            ),
            error: `Layer "${layer.name}" is locked and cannot be modified`,
          };
        }

        // Use LLM to parse the natural language request
        const {transformParams, unsupportedRequest} = await parseTransformFromNaturalLanguage(
          params.transformExplained,
          activeComp.w,
          activeComp.h,
          abortSignal,
        );

        // Determine if 3D is needed
        const needs3D = !!(
          (transformParams.position &&
            transformParams.position.some((kf: AnimatedKeyframeInput) => kf.z !== undefined)) ||
          (transformParams.anchor &&
            transformParams.anchor.some((kf: AnimatedKeyframeInput) => kf.z !== undefined)) ||
          (transformParams.scale &&
            transformParams.scale.some((kf: AnimatedKeyframeInput) => kf.z !== undefined)) ||
          transformParams.rotationX ||
          transformParams.rotationY
        );

        const updatedTransform: Partial<MovieLayer['tr']> = {};
        const changes: string[] = [];

        // Apply the parsed transform properties (same logic as original tool)
        if (transformParams.position) {
          updatedTransform.p = buildKeyframedValue(
            transformParams.position as AnimatedKeyframeInput[],
            3,
            {
              propertyName: 'Position',
            },
          ) as NumberArr<3>;
          changes.push('position');
        }

        if (transformParams.anchor) {
          updatedTransform.an = buildKeyframedValue(
            transformParams.anchor as AnimatedKeyframeInput[],
            3,
            {
              propertyName: 'Anchor',
            },
          ) as NumberArr<3>;
          changes.push('anchor');
        }

        if (transformParams.scale) {
          updatedTransform.s = buildKeyframedValue(
            transformParams.scale as AnimatedKeyframeInput[],
            3,
            {
              isScale: true,
              propertyName: 'Scale',
            },
          ) as NumberArr<3>;
          changes.push('scale');
        }

        if (transformParams.rotationX) {
          updatedTransform.rx = buildKeyframedValue(
            transformParams.rotationX as AnimatedKeyframeInput[],
            1,
            {
              isRotation: true,
              propertyName: 'Rotation X',
            },
          ) as NumberArr<1>;
          changes.push('rotationX');
        }

        if (transformParams.rotationY) {
          updatedTransform.ry = buildKeyframedValue(
            transformParams.rotationY as AnimatedKeyframeInput[],
            1,
            {
              isRotation: true,
              propertyName: 'Rotation Y',
            },
          ) as NumberArr<1>;
          changes.push('rotationY');
        }

        if (transformParams.rotationZ) {
          updatedTransform.rz = buildKeyframedValue(
            transformParams.rotationZ as AnimatedKeyframeInput[],
            1,
            {
              isRotation: true,
              propertyName: 'Rotation Z',
            },
          ) as NumberArr<1>;
          changes.push('rotationZ');
        }

        if (transformParams.opacity) {
          updatedTransform.a = buildKeyframedValue(
            transformParams.opacity as AnimatedKeyframeInput[],
            1,
            {
              isOpacity: true,
              propertyName: 'Opacity',
            },
          ) as NumberArr<1>;
          changes.push('opacity');
        }

        // Apply the updates
        if (Object.keys(updatedTransform).length > 0) {
          store.dispatch(
            updateLayer({
              layerId: params.layerId,
              data: {
                tr: {
                  ...layer.tr,
                  ...updatedTransform,
                },
                ...(needs3D && !layer.is3d ? {is3d: true} : {}),
              },
            }),
          );

          return {
            content: `Updated ${changes.join(', ')} for layer id "${layer.id}"${needs3D && !layer.is3d ? ' (3D enabled)' : ''}${unsupportedRequest ? ` (Note: ${unsupportedRequest})` : ''}`,
          };
        }

        return {
          content: 'No transform properties were parsed from the description',
        };
      } catch (error) {
        return {
          content: `Failed to update layer transform: ${error instanceof Error ? error.message : 'Unknown error'}`,
        };
      }
    },
  });
}

export default editLayerTransform2Tool;
